import { ENTRY_KIND_PAYMENT } from './nominaEntry';

// Espejo de `NOMINA_KIND_*` en el backend. La particular es el recibo de UN trabajador;
// la de resumen, los totales de toda la plantilla en un PDF: no es de nadie, no se enlaza
// a ninguna ficha y se contabiliza con las cuentas de la empresa.
export const NOMINA_KIND_INDIVIDUAL = 'individual';
export const NOMINA_KIND_SUMMARY = 'summary';
/** La cabecera del resumen que la IA leyó pero no llevó a ningún campo. */
export const SUMMARY_IGNORED_FIELD = 'ignore';
// Espejo de `NOMINA_FILE_TYPE_*`: el Excel lo abre ADP y solo puede ser un resumen.
export const NOMINA_FILE_TYPE_XLSX = 'xlsx';
/** Lo que se cobra de un Excel, como el backend: una nómina de una página. */
export const EXCEL_BILLED_PAGE_COUNT = 1;
// Cómo va el líquido de un resumen (espejo de `SUMMARY_NET_DETAIL_*`).
export const SUMMARY_NET_DETAIL_PER_EMPLOYEE = 'per_employee';
export const SUMMARY_NET_DETAIL_SINGLE = 'single';

/** Las extensiones de Excel: el de siempre y el antiguo. Espejo de `EXCEL_EXTENSION_LIST`. */
export const EXCEL_EXTENSION_LIST = ['.xlsx', '.xls'];

/** Si el fichero es un Excel, por su extensión (como `nomina_file_type` del backend). */
export function isExcelFile(filename: string): boolean {
  const lowered = (filename || '').trim().toLowerCase();
  return EXCEL_EXTENSION_LIST.some(extension => lowered.endsWith(extension));
}

/** El líquido de un trabajador dentro de un resumen. */
export interface NominaSummaryEmployeeNet {
  employee_code: string;
  employee_name: string;
  employee_dni: string;
  net_amount: number;
  /**
   * La ficha con la que se enlazó al leerlo (resumen con una línea por trabajador). Vacío =
   * no está en el maestro: hay que darle de alta antes de contabilizar.
   */
  empleado_uuid?: string;
}

/** Un trabajador del resumen que no se pudo dar de alta, y por qué. */
export interface NominaSummaryEmpleadoRejection {
  label: string;
  reason: string;
}

/** Lo que pasó al dar de alta de golpe a los trabajadores que faltaban. */
export interface NominaSummaryEmpleadoCreationResult {
  createdCount: number;
  missingCount: number;
  state: string;
  errorMessage: string;
  rejectedList: NominaSummaryEmpleadoRejection[];
}

/**
 * Cómo va el líquido de un resumen: lo que eligió la nómina o, si nada, lo heredado (la
 * empresa, y si tampoco, en una línea). Espejo de `resolve_summary_net_detail`.
 */
export function effectiveSummaryNetDetail(nomina: { summary_net_detail?: string }, inheritedValue: string): string {
  const own = nomina.summary_net_detail || '';
  if (own === SUMMARY_NET_DETAIL_PER_EMPLOYEE || own === SUMMARY_NET_DETAIL_SINGLE) return own;
  return inheritedValue === SUMMARY_NET_DETAIL_PER_EMPLOYEE ? SUMMARY_NET_DETAIL_PER_EMPLOYEE : SUMMARY_NET_DETAIL_SINGLE;
}

/** Si el resumen lleva una línea de líquido por trabajador (suya o heredada). */
export function isSummaryPerEmployee(
  nomina: { nomina_kind?: string; summary_net_detail?: string }, inheritedValue = '',
): boolean {
  return isSummaryNomina(nomina) && effectiveSummaryNetDetail(nomina, inheritedValue) === SUMMARY_NET_DETAIL_PER_EMPLOYEE;
}

/** Una cabecera del resumen y el campo de la nómina al que la llevó la IA. */
export interface NominaSummaryHeaderMapping {
  header: string;
  field: string;
}

export interface Nomina {
  // First-class domain identity ('' for a not-yet-persisted nómina). The Api
  // repo maps the entity-store row's uuid into/out of this field.
  uuid: string;
  /** Cuándo se subió (ISO). De la fila: lo pone el almacén y no se edita. */
  created_at?: string;
  /** Particular o resumen (`NOMINA_KIND_*`). '' = particular, como todas las antiguas. */
  nomina_kind?: string;
  /** Solo en un resumen: qué cabecera del PDF fue a qué campo. */
  summary_header_map?: NominaSummaryHeaderMapping[];
  /** Solo en un resumen: el líquido de cada trabajador. */
  summary_employee_net_list?: NominaSummaryEmployeeNet[];
  /** Solo en un resumen: si se subió con una línea de líquido por trabajador (`SUMMARY_NET_DETAIL_*`). */
  summary_net_detail?: string;
  /** PDF o Excel (`NOMINA_FILE_TYPE_*`). '' = PDF. */
  source_file_type?: string;
  // Employee of the master list this payroll belongs to, resolved by the parse
  // from the DNI the AI read off the PDF ('' when it could not be matched, in
  // which case the nómina is in `error`).
  empleado_uuid: string;
  employee_name: string;
  employee_dni: string;
  period: string;
  payment_date: string;
  gross_amount: number;
  net_amount: number;
  irpf_amount: number;
  ss_employee_amount: number;
  /**
   * Deducciones que no son IRPF ni SS: especie, anticipos, embargos… 0 en casi todas las
   * nóminas, y entonces ni se enseña ni llega al asiento.
   */
  other_deductions_amount?: number;
  /**
   * La parte del bruto pagada en especie. Ya está dentro de `gross_amount`: no entra en
   * el cuadre, solo saca esa parte de la 640 a su propia cuenta (6401).
   */
  in_kind_amount?: number;
  /** Cuánto de las otras deducciones es de cada tipo (`DEDUCTION_TYPE_LIST`). */
  deduction_in_kind_amount?: number;
  deduction_advance_amount?: number;
  deduction_staff_loan_amount?: number;
  deduction_garnishment_amount?: number;
  deduction_union_fee_amount?: number;
  /** Si las otras deducciones van desglosadas o en una línea. '' = lo heredado. */
  deductions_detail?: string;
  /** El tipo de IRPF (%), para calcular el IRPF de la especie. */
  irpf_rate?: number;
  /** Si el IRPF va desglosado (dinero + especie) o en una línea. '' = lo heredado. */
  irpf_detail?: string;
  /** Las cotizaciones de la SS por concepto, editables. El total es `ss_*_amount`. */
  ss_employee_line_map?: { [key: string]: number };
  ss_employer_line_map?: { [key: string]: number };
  /** Si cada SS va desglosada o en una línea. '' = lo heredado. */
  ss_employee_detail?: string;
  ss_employer_detail?: string;
  /** Con las dos SS en una línea, si la 476 va separada por cuota. '' = lo heredado. */
  ss_bodies_detail?: string;
  /** Qué asientos se suben para ESTA nómina. '' = lo del empleado o la empresa. */
  entry_mode?: string;
  /** Completo o simplificado (sin la SS de la empresa). '' = lo heredado. */
  accounting_method?: string;
  /** Si el bruto va desglosado (640 + 6401) o en una línea. '' = lo del empleado o la empresa. */
  gross_detail?: string;
  /**
   * Los campos que la IA CALCULÓ en vez de copiarlos del PDF (p. ej. la SS de la empresa
   * como coste empresa − bruto). El resto salió tal cual del documento.
   */
  computed_field_list?: string[];
  /**
   * Cómo sacó ADP la SS empresa: `source` (`company_cost` / `breakdown` / `read`), las
   * cifras usadas, el desglose y si no cuadraba con el coste empresa (`warning`).
   */
  employer_contribution?: {
    source?: string;
    amount?: number;
    breakdown_total?: number;
    company_cost_total?: number;
    company_cost?: number;
    is_mismatch?: boolean;
    warning?: string;
    breakdown_line_list?: Array<{ field: string; label: string; amount: number }>;
  };
  /**
   * Lo mismo para la SS trabajador: `source` (`read` si el total estaba escrito,
   * `breakdown` si ADP sumó las cotizaciones), las líneas y si no cuadraban (`warning`).
   */
  employee_contribution?: {
    source?: string;
    amount?: number;
    breakdown_total?: number;
    is_mismatch?: boolean;
    warning?: string;
    breakdown_line_list?: Array<{ field: string; label: string; amount: number }>;
  };
  ss_employer_amount: number;
  // Datos del TRABAJADOR que la IA pudo leer del papel. No entran en el asiento:
  // solo sirven para prerrellenar la ficha del empleado que falta, desde el propio
  // detalle de la nómina (ver `buildEmpleadoFromNomina`). Normalmente vacíos — el
  // recibo estándar (RD 2317/1993) trae la antigüedad y el grupo profesional, y ni
  // eso siempre; email y teléfono casi nunca.
  employee_hire_date: string;
  employee_job_title: string;
  employee_email: string;
  employee_phone: string;
  /**
   * Lo que la IA leyó EN SU DÍA, congelado. No se toca al editar la nómina.
   *
   * Es el registro con el que se resuelve una incidencia: «¿esto lo leyó así la IA o lo
   * cambió alguien?». Vacío = esta nómina no ha pasado por la IA (subida a mano, o antes
   * de que existiera el registro). Ver `buildAiReadComparisonRowList`.
   */
  ai_read_snapshot: Record<string, any>;
  /** El asiento tal como lo generó el sistema, para comparar con el de ahora. */
  generated_entry_snapshot?: Array<{ kind: string; description: string; line_list: any[] }>;
  state: string;
  pdf_path: string;
  pdf_url: string;
  source_filename: string;
  error_message: string;
  entry_reference: string;
  /**
   * La referencia que el programa contable dio a CADA asiento. Vacías hasta que las
   * devuelve — son dos asientos y cada uno tiene la suya.
   */
  erp_registration_reference?: string;
  erp_payment_reference?: string;
  // Orientación en la que está GUARDADO el PDF (0/90/180/270). Girar reescribe
  // el fichero en S3 (como en contabilidad), así que el visor no tiene que
  // rotar nada: pinta el PDF tal como está. Esto solo sirve para saber de qué
  // ángulo se parte al pedir el siguiente giro.
  pdf_rotation: number;
  /**
   * Si una persona cambió el asiento a mano. Se advierte antes de contabilizar: al
   * programa contable no sube lo que el sistema calculó de la nómina.
   */
  is_entry_edited: boolean;
  /**
   * Separador inteligente. Una sesión del agente por intento, la más reciente al final:
   * mientras la nómina está «En proceso» con sesión, se está SEPARANDO (no leyendo).
   */
  split_session_list?: NominaSplitSession[];
  /** Cortes propuestos para revisar: [primera, última] 1-indexados, como facturas. */
  split_range_list?: number[][];
  /** Aviso si el separador falló: {error_code, refunded, refunded_credits, at}. */
  split_error?: NominaSplitError | null;
}

export interface NominaSplitSession {
  session_id: string;
  started_at: string;
}

export interface NominaSplitError {
  error_code: string;
  refunded: boolean;
  refunded_credits: number;
  at: string;
}

export const NOMINA_STATE_PROCESSING = 'processing';
/**
 * El separador inteligente propone dónde empieza cada nómina: el cliente revisa los
 * cortes sobre el PDF, los ajusta si quiere y los acepta. Espejo de
 * `NOMINA_STATE_PENDING_SPLIT_REVIEW` en el backend. NO es trabajo en curso: espera al
 * cliente, así que la pantalla no la vigila.
 */
export const NOMINA_STATE_PENDING_SPLIT_REVIEW = 'pending_split_review';
/**
 * Subida pero sin cobrar: el saldo no llegaba. Espera a que el cliente recargue y pulse
 * «Procesar». Espejo de `NOMINA_STATE_PENDING_PAYMENT` en el backend. La cabecera la
 * cuenta dentro de «En proceso» (`CHIP_STATE_GROUP_MAP`).
 */
export const NOMINA_STATE_PENDING_PAYMENT = 'pending_payment';
export const NOMINA_STATE_ERROR = 'error';
export const NOMINA_STATE_PENDING_REVIEW = 'pending_review';
export const NOMINA_STATE_ENTRY_GENERATED = 'entry_generated';
export const NOMINA_STATE_ACCOUNTING = 'accounting';
/**
 * El conector YA cogió la operación y la está subiendo al ERP.
 *
 * Distinto de `accounting`, donde solo está en cola: una nómina que lleva diez minutos
 * ahí puede estar encallada, mientras que en «Contabilizando…» alguien está trabajando
 * en ella ahora mismo. Para el usuario es el MISMO punto del ciclo, así que comparten
 * chip de cabecera y color; lo que las distingue es la etiqueta.
 */
export const NOMINA_STATE_ACCOUNTING_RUNNING = 'accounting_running';
/**
 * El conector generó el fichero .DAT, pero A3 todavía no lo ha importado.
 *
 * El conector A3 trabaja de dos maneras y las dos conviven en producción: escribiendo
 * directamente (automático) o dejando un `.DAT` que alguien importa después. En el
 * segundo caso el asiento salió de aquí pero el ERP no lo ha visto ni le ha dado número.
 *
 * De la familia de «Contabilizando» —el asiento ya salió— pero **terminal**: no hay
 * callback que avise de que el fichero se importó, así que la pantalla NO lo vigila y lo
 * cierra el cliente, que es quien hace la importación.
 */
export const NOMINA_STATE_PENDING_SYNC = 'pending_sync';
export const NOMINA_STATE_ACCOUNTED = 'accounted';
/**
 * Cerrada a mano: alguien la contabilizó FUERA del programa (o de una forma que el
 * runner no puede confirmar). Es de la familia de "contabilizada" —el ciclo terminó—
 * pero NO es `accounted`: ese lo declara el programa contable al aceptar el asiento,
 * y confundirlos afirmaría que el ERP la aceptó cuando nadie se lo preguntó.
 */
export const NOMINA_STATE_ACCOUNTED_MANUAL = 'accounted_manual';

export const NOMINA_STATE_LABEL_MAP: Record<string, string> = {
  [NOMINA_STATE_PROCESSING]: 'En proceso',
  [NOMINA_STATE_PENDING_SPLIT_REVIEW]: 'Revisar separación',
  [NOMINA_STATE_PENDING_PAYMENT]: 'Sin créditos',
  [NOMINA_STATE_ERROR]: 'Errores',
  [NOMINA_STATE_PENDING_REVIEW]: 'Pend. de revisión',
  [NOMINA_STATE_ENTRY_GENERATED]: 'Asiento generado',
  [NOMINA_STATE_ACCOUNTING]: 'Contabilizando',
  [NOMINA_STATE_ACCOUNTING_RUNNING]: 'Contabilizando…',
  [NOMINA_STATE_PENDING_SYNC]: 'Pend. de sincronización',
  [NOMINA_STATE_ACCOUNTED]: 'Contabilizada',
  [NOMINA_STATE_ACCOUNTED_MANUAL]: 'Contabilizada manualmente',
};

export const NOMINA_STATE_COLOR_MAP: Record<string, string> = {
  [NOMINA_STATE_PROCESSING]: 'warning',
  // El azul de «Pend. de revisión»: también espera al cliente, no avanza sola.
  [NOMINA_STATE_PENDING_SPLIT_REVIEW]: 'info',
  // Rojo y no el ámbar de «En proceso»: esta NO avanza sola, espera al cliente.
  [NOMINA_STATE_PENDING_PAYMENT]: 'error',
  [NOMINA_STATE_ERROR]: 'error',
  [NOMINA_STATE_PENDING_REVIEW]: 'info',
  // Primary y no success: el asiento existe pero NO está contabilizado; darle el
  // verde de "hecho" invitaría a pensar que ya llegó al programa contable.
  [NOMINA_STATE_ENTRY_GENERATED]: 'primary',
  [NOMINA_STATE_ACCOUNTING]: 'warning',
  // El MISMO ámbar: el color dice en qué punto está la nómina, no quién la mueve.
  [NOMINA_STATE_ACCOUNTING_RUNNING]: 'warning',
  [NOMINA_STATE_PENDING_SYNC]: 'warning',
  [NOMINA_STATE_ACCOUNTED]: 'success',
  // El MISMO verde que `accounted`, no un teal aparte. El color en este módulo dice en qué
  // punto está la nómina, no cómo llegó: las dos son «ya está resuelta», y la cabecera ya
  // las agrupa en un solo chip (`CHIP_STATE_GROUP_MAP`). Con dos verdes distintos el
  // listado sugería dos situaciones donde hay una.
  //
  // Quién la cerró lo dice la ETIQUETA («Contabilizada manualmente»), que es donde cabe
  // el matiz sin gastar un color en él.
  [NOMINA_STATE_ACCOUNTED_MANUAL]: 'success',
};

export interface NominaStateOption {
  text: string;
  value: string;
}

// En orden del ciclo de vida: es el orden en que se pintan los chips de cabecera.
export const NOMINA_STATE_OPTION_LIST: NominaStateOption[] = [
  { text: 'En proceso', value: NOMINA_STATE_PROCESSING },
  { text: 'Revisar separación', value: NOMINA_STATE_PENDING_SPLIT_REVIEW },
  { text: 'Sin créditos', value: NOMINA_STATE_PENDING_PAYMENT },
  { text: 'Errores', value: NOMINA_STATE_ERROR },
  { text: 'Pend. de revisión', value: NOMINA_STATE_PENDING_REVIEW },
  { text: 'Asiento generado', value: NOMINA_STATE_ENTRY_GENERATED },
  { text: 'Contabilizando', value: NOMINA_STATE_ACCOUNTING },
  { text: 'Contabilizando…', value: NOMINA_STATE_ACCOUNTING_RUNNING },
  { text: 'Pend. de sincronización', value: NOMINA_STATE_PENDING_SYNC },
  { text: 'Contabilizada', value: NOMINA_STATE_ACCOUNTED },
  { text: 'Contabilizada manualmente', value: NOMINA_STATE_ACCOUNTED_MANUAL },
];

/** Todos los estados, en orden del ciclo de vida. Espejo de `NOMINA_STATE_LIST`. */
export const NOMINA_STATE_LIST: string[] = NOMINA_STATE_OPTION_LIST.map(option => option.value);

/**
 * Los estados que cada chip de cabecera representa.
 *
 * Casi todos son uno solo, pero **«Contabilizada» agrupa la familia**: `accounted` (lo
 * declaró el programa contable) y `accounted_manual` (lo cerró una persona). Para el
 * usuario son lo mismo en la cabecera —"ya está resuelta"—, y partirlo en dos chips
 * rompe ese grupo mental y deja dos números que hay que sumar a mano.
 *
 * En la FILA sí se distinguen (etiqueta y color propios): ahí lo que importa es quién
 * la cerró.
 */
const CHIP_STATE_GROUP_MAP: Record<string, string[]> = {
  // «En proceso» incluye las que esperan saldo: para el cliente son "subidas y todavía
  // sin leer", y es ahí donde tiene el botón «Procesar». En «Errores» se confundían con
  // las que hay que corregir a mano, que llevan otro camino.
  [NOMINA_STATE_PROCESSING]: [NOMINA_STATE_PROCESSING, NOMINA_STATE_PENDING_PAYMENT],
  [NOMINA_STATE_ACCOUNTED]: [NOMINA_STATE_ACCOUNTED, NOMINA_STATE_ACCOUNTED_MANUAL],
  // «Contabilizando» agrupa la cola y lo que el conector ya está subiendo: en la
  // cabecera los dos son "esto lo tiene el programa contable", y dos chips dejarían
  // dos números que sumar a mano.
  [NOMINA_STATE_ACCOUNTING]: [
    NOMINA_STATE_ACCOUNTING,
    NOMINA_STATE_ACCOUNTING_RUNNING,
    NOMINA_STATE_PENDING_SYNC,
  ],
};

// ---- Datos mínimos para generar el asiento -----------------------------------
//
// Espejo de `Nomina.validate_for_entry()`. Se duplica como el resto de la validación
// del módulo: aquí para poder decir QUÉ falta antes de pulsar, y el backend lo vuelve
// a comprobar porque el bus se despacha desde cualquier sitio.
//
// Existe porque se generó un asiento de una nómina vacía: sin período, sin fecha de
// pago y con todo a 0. La referencia salió `NOMINA-sin-periodo-...`.

/**
 * Tolerancia del cuadre, en euros. Espejo de `ENTRY_BALANCE_TOLERANCE`.
 *
 * Los importes salen de aplicar porcentajes (IRPF, cuotas), así que exigir igualdad
 * exacta bloquearía nóminas correctas. Se compara con `>`, no `>=`: un descuadre de
 * EXACTAMENTE un céntimo es el caso típico del redondeo y hay que tolerarlo.
 */
const NET_BALANCE_TOLERANCE = 0.01;

function isValidPeriod(period: string): boolean {
  if (!/^\d{4}-\d{2}$/.test(period)) return false;
  const month = parseInt(period.slice(5, 7), 10);
  return month >= 1 && month <= 12;
}

function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(value + 'T00:00:00');
  return !Number.isNaN(parsed.getTime());
}

/**
 * Qué falta o no cuadra para generar el asiento. `[]` = se puede.
 *
 * Devuelve TODOS los problemas, no para en el primero: corregir uno y descubrir el
 * siguiente al reintentar es peor que verlos juntos.
 *
 * NO exige empleado enlazado: las cuentas del asiento caen a las de la empresa y, en
 * su defecto, a las del PGC — esa cascada existe justo para eso.
 */
export function validateNominaForEntry(nomina: Nomina): string[] {
  const errorList: string[] = [];
  const period = (nomina.period || '').trim();
  if (!period) {
    errorList.push('Falta el período: el asiento se fecha por él.');
  } else if (!isValidPeriod(period)) {
    errorList.push('El período «' + period + '» no es válido (AAAA-MM).');
  }

  const paymentDate = (nomina.payment_date || '').trim();
  if (!paymentDate) {
    errorList.push('Falta la fecha de pago: es la fecha de la segunda partida del asiento.');
  } else if (!isValidIsoDate(paymentDate)) {
    errorList.push('La fecha de pago «' + paymentDate + '» no es válida (AAAA-MM-DD).');
  } else {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (new Date(paymentDate + 'T00:00:00') > today) {
      errorList.push('La fecha de pago «' + paymentDate + '» es posterior a hoy: se '
        + 'contabilizaría en un ejercicio que todavía no existe.');
    } else if (period && isValidPeriod(period) && paymentDate.slice(0, 7) < period) {
      errorList.push('La fecha de pago «' + paymentDate + '» es anterior al período '
        + period + ': no se paga una nómina antes de devengarla.');
    }
  }

  const amountList = [
    nomina.gross_amount, nomina.net_amount, nomina.irpf_amount,
    nomina.ss_employee_amount, nomina.ss_employer_amount, nomina.other_deductions_amount,
    nomina.in_kind_amount,
  ].map(amount => amount || 0);
  if (amountList.some(amount => amount < 0)) {
    errorList.push('Hay importes negativos: invertirían el asiento.');
  } else if ((nomina.in_kind_amount || 0) > (nomina.gross_amount || 0) + NET_BALANCE_TOLERANCE) {
    // Espejo de `ENTRY_IN_KIND_ABOVE_GROSS_MESSAGE`: la 640 saldría negativa.
    errorList.push('La retribución en especie supera el salario bruto: es una parte del '
      + 'bruto, no puede ser mayor.');
  } else if (!amountList.some(amount => amount > 0)) {
    errorList.push('Todos los importes están a cero: el asiento no describiría nada.');
  } else {
    // Cuadre: bruto − IRPF − SS trabajador tiene que dar el líquido. La pantalla ya lo
    // avisaba, pero solo de forma informativa: una nómina descuadrada en 20 € pasó por
    // todos los estados hasta contabilizarse, y eso descuadra el programa contable.
    //
    // La SS a cargo de la EMPRESA no entra: no se le descuenta al trabajador, e
    // incluirla marcaría como descuadradas todas las nóminas correctas.
    const expectedNet = nominaExpectedNet(nomina);
    const difference = Math.round((expectedNet - (nomina.net_amount || 0)) * 100) / 100;
    // Un céntimo es redondeo (los importes salen de aplicar porcentajes), no un error.
    if (Math.abs(difference) > NET_BALANCE_TOLERANCE) {
      errorList.push('Los importes no cuadran: bruto − IRPF − SS trabajador − otras '
        + 'deducciones difiere del neto en ' + difference.toFixed(2) + ' €.');
    }
  }

  // Un resumen es de toda la empresa: no tiene ficha que enlazar (espejo del backend).
  if (!isSummaryNomina(nomina) && !(nomina.empleado_uuid || '').trim()) {
    // El parse solo lleva a revisión CON empleado enlazado: sin él deja la nómina en
    // `error` a propósito. Y la cascada de cuentas del asiento empieza por el empleado,
    // así que sin ficha se contabilizaría contra las genéricas de la empresa sin que
    // nadie lo haya decidido.
    errorList.push('La nómina no tiene empleado enlazado: elija el trabajador o dé de '
      + 'alta su ficha.');
  }
  return errorList;
}

/** Los estados que hay que contar y filtrar para un chip de cabecera. */
export function statesForStateChip(chipState: string): string[] {
  return CHIP_STATE_GROUP_MAP[chipState] || [chipState];
}

/**
 * Los estados que llevan chip propio en la cabecera.
 *
 * Se excluyen los que ya están agrupados bajo otro: `accounted_manual` se cuenta en
 * «Contabilizada», así que un chip suyo lo contaría dos veces.
 */
export function stateChipStateList(): string[] {
  const groupedStateList: string[] = [];
  Object.keys(CHIP_STATE_GROUP_MAP).forEach((chipState) => {
    CHIP_STATE_GROUP_MAP[chipState].forEach((state) => {
      if (state !== chipState) groupedStateList.push(state);
    });
  });
  return NOMINA_STATE_LIST.filter(state => groupedStateList.indexOf(state) < 0);
}

// ---- Cambio MANUAL de estado (el desplegable de cambios masivos) -------------
//
// Espejo de `MANUAL_STATE_CHANGE_MAP` en `ddd/nominas/domain/nomina.py`. Se duplica
// a propósito, como el resto de la validación del módulo: el desplegable solo ofrece
// lo alcanzable, y el backend lo vuelve a comprobar porque el bus se despacha desde
// cualquier sitio.
//
// Las tres reglas:
//
// 1. **No se SUBE.** Subir sería afirmar un trabajo que nadie hizo — una nómina en
//    `error` puesta en `entry_generated` dice tener un asiento inexistente, y
//    contabilizarla mandaría al ERP un asiento vacío.
// 2. **`processing`, `error` y `accounted` no se ponen a mano.** Los tres los declara
//    un proceso, no una persona: `processing` es "la IA está leyendo AHORA" (para
//    releer está «Reintentar IA»); `error` es "la IA no pudo con este PDF", y marcarlo
//    a mano inventa un fallo que no existió; `accounted` lo declara el programa
//    contable al aceptar el asiento.
// 3. **`accounted_manual` es la salida de emergencia**, alcanzable desde cualquier
//    estado no bloqueado: el "ya está resuelto por fuera, no me la sigas mostrando".
//
// A un CLIENTE le queda poco margen, y es a propósito: el ciclo de vida lo llevan la
// IA y el runner. Soporte no tiene estos límites (ver `isStaff`).
const MANUAL_STATE_CHANGE_MAP: Record<string, string[]> = {
  // La IA está escribiendo en ella: nada. Ni cerrarla a mano, porque el parse
  // aterrizaría encima y el usuario perdería el resultado sin verlo.
  [NOMINA_STATE_PROCESSING]: [],
  // Esperando a que acepte los cortes: solo cerrarla. Avanzar es «Aceptar separación».
  [NOMINA_STATE_PENDING_SPLIT_REVIEW]: [NOMINA_STATE_ACCOUNTED_MANUAL],
  // Sin cobrar: solo cerrarla («ya no la quiero»). Todo lo demás es dar servicio.
  [NOMINA_STATE_PENDING_PAYMENT]: [NOMINA_STATE_ACCOUNTED_MANUAL],
  [NOMINA_STATE_ERROR]: [NOMINA_STATE_ACCOUNTED_MANUAL],
  [NOMINA_STATE_PENDING_REVIEW]: [NOMINA_STATE_ACCOUNTED_MANUAL],
  [NOMINA_STATE_ENTRY_GENERATED]: [
    NOMINA_STATE_PENDING_REVIEW,
    NOMINA_STATE_ACCOUNTED_MANUAL,
  ],
  // El worker externo la tiene en la mano. Solo desatascarla, y solo staff.
  [NOMINA_STATE_ACCOUNTING]: [NOMINA_STATE_ACCOUNTED_MANUAL],
  // Y aquí la está subiendo AHORA: con más razón.
  [NOMINA_STATE_ACCOUNTING_RUNNING]: [NOMINA_STATE_ACCOUNTED_MANUAL],
  // El conector ya la soltó: el fichero está hecho y lo importa el CLIENTE, así que es
  // quien sabe cuándo cerrarla. No se restringe a staff (ver STAFF_ONLY, que lo excluye).
  [NOMINA_STATE_PENDING_SYNC]: [NOMINA_STATE_ACCOUNTED_MANUAL],
  // Volver a revisión, para el cliente que vio que el asiento subido no era el que
  // esperaba. La nómina suelta su asiento (que sigue en su programa contable) y genera
  // uno nuevo; el diálogo le hace confirmar que lo sabe (`stateChangeDetachesTheEntry`).
  [NOMINA_STATE_ACCOUNTED]: [NOMINA_STATE_ACCOUNTED_MANUAL, NOMINA_STATE_PENDING_REVIEW],
  [NOMINA_STATE_ACCOUNTED_MANUAL]: [],
};

/**
 * Estados de partida cuyo cambio manual queda reservado a staff.
 *
 * `accounting` está aquí porque el runner del programa contable la tiene: si murió a
 * medias, soporte tiene que poder cerrarla, pero un cliente no debería tocar una
 * nómina que quizá SÍ llegó al ERP.
 */
const STAFF_ONLY_MANUAL_STATE_LIST = [
  NOMINA_STATE_ACCOUNTING,
  NOMINA_STATE_ACCOUNTING_RUNNING,
];

/**
 * Los estados a los que `currentState` puede pasar a mano ([] = ninguno).
 *
 * **Soporte (`isStaff`) puede llevar cualquier nómina a cualquier estado.** Es la vía
 * de escape para desatascar lo que la máquina de estados no previó; las reglas del
 * mapa existen para que el CLIENTE no se rompa el ciclo de vida sin saberlo. Lo único
 * que no se ofrece es el estado en el que ya está: no es un cambio.
 */
export function allowedManualStateList(currentState: string, isStaff = false): string[] {
  if (isStaff) {
    return NOMINA_STATE_LIST.filter(state => state !== currentState);
  }
  return MANUAL_STATE_CHANGE_MAP[currentState] || [];
}

export function isManualStateChangeAllowed(
  currentState: string, targetState: string, isStaff = false,
): boolean {
  return allowedManualStateList(currentState, isStaff).indexOf(targetState) >= 0;
}

export function isStaffOnlyManualStateChange(currentState: string): boolean {
  return STAFF_ONLY_MANUAL_STATE_LIST.indexOf(currentState) >= 0;
}

/** Los estados en los que la nómina TIENE asiento. Espejo del backend. */
const NOMINA_STATE_WITH_ENTRY_LIST = [
  NOMINA_STATE_ENTRY_GENERATED,
  NOMINA_STATE_ACCOUNTING,
  NOMINA_STATE_ACCOUNTING_RUNNING,
  NOMINA_STATE_PENDING_SYNC,
  NOMINA_STATE_ACCOUNTED,
  NOMINA_STATE_ACCOUNTED_MANUAL,
];

/**
 * Si este cambio de estado deja el asiento sin sentido y el backend lo BORRARÁ.
 *
 * Espejo de `state_change_drops_the_entry`. Aquí solo sirve para AVISAR antes de
 * pulsar: borrar un asiento de contabilidad es un efecto en otro sistema, y no puede
 * descubrirse después.
 *
 * Dos casos en los que NO se borra: cerrar a mano (`accounted_manual`, que conserva el
 * registro contable) y partir de `accounting`/`accounted` (ese asiento ya viajó al ERP
 * y tocarlo lo descuadraría).
 */
export function stateChangeDropsTheEntry(currentState: string, targetState: string): boolean {
  if (currentState !== NOMINA_STATE_ENTRY_GENERATED) return false;
  return NOMINA_STATE_WITH_ENTRY_LIST.indexOf(targetState) < 0;
}

/**
 * Si el cambio SUELTA el asiento de la nómina sin borrarlo: una contabilizada que vuelve
 * a revisión. El asiento ya está en el programa contable del cliente y ahí sigue; la
 * nómina se queda sin él y genera uno nuevo. Espejo de `state_change_detaches_the_entry`.
 */
export function stateChangeDetachesTheEntry(currentState: string, targetState: string): boolean {
  return currentState === NOMINA_STATE_ACCOUNTED && targetState === NOMINA_STATE_PENDING_REVIEW;
}

/**
 * Los destinos que sirven para TODAS las nóminas seleccionadas (la intersección).
 *
 * Se intersecan y no se unen: ofrecer un destino que solo vale para una parte de la
 * selección dejaría la operación a medias sin que el usuario lo esperase. Con una
 * sola nómina en `processing` la lista sale vacía, que es lo correcto.
 */
export function allowedManualStateListForAll(
  currentStateList: string[], isStaff = false,
): string[] {
  if (!currentStateList.length) return [];
  return currentStateList.reduce(
    (common: string[], state: string) => common.filter(
      target => isManualStateChangeAllowed(state, target, isStaff),
    ),
    allowedManualStateList(currentStateList[0], isStaff),
  );
}

export const NOMINA_STATE_MISSING_LABEL = 'Sin estado';

export function getNominaStateLabel(state: string): string {
  // Un estado ausente se nombra: un chip en blanco no dice nada al usuario.
  return NOMINA_STATE_LABEL_MAP[state] || state || NOMINA_STATE_MISSING_LABEL;
}

export function getNominaStateColor(state: string): string {
  return NOMINA_STATE_COLOR_MAP[state] || 'grey';
}

/** El único paso que la nómina puede dar desde su estado actual. */
export interface NominaAdvanceAction {
  /** Texto del botón: el usuario lee QUÉ va a pasar, no el nombre del estado. */
  label: string;
  icon: string;
  color: string;
  /** Acción de permiso que hay que tener para poder pulsarlo. */
  requiredAction: string;
  /** Qué método del componente ejecuta el paso. */
  handlerName: string;
  /** Ayuda del tooltip: qué consigue y qué pasa después. */
  hint: string;
}

/**
 * Un único botón cuyo significado depende del estado. Es la máquina de estados
 * del backend contada al usuario: en cada momento hay UNA cosa sensata que
 * hacer, así que se ofrece esa y ninguna más.
 *
 * `null` = no hay paso que dar: en `processing` la IA está trabajando y en
 * `accounting` el runner tiene el asiento; en `accounted` el ciclo terminó.
 */
export function getNominaAdvanceAction(state: string): NominaAdvanceAction | null {
  return NOMINA_ADVANCE_ACTION_MAP[state] || null;
}

const NOMINA_ADVANCE_ACTION_MAP: Record<string, NominaAdvanceAction> = {
  // Las que se subieron sin saldo. Cobra y manda a leer: de ahí salen a «Pend. de
  // revisión», o a «Errores» si algún dato no se pudo leer. Va en el chip «En proceso»,
  // que es donde se cuentan (`CHIP_STATE_GROUP_MAP`).
  [NOMINA_STATE_PENDING_PAYMENT]: {
    label: 'Procesar',
    icon: 'mdi-play-circle-outline',
    color: 'primary',
    requiredAction: 'nomina_upload',
    handlerName: 'processNomina',
    hint: 'Cobra los créditos de su paquete combinado y manda la nómina a leer con la IA.',
  },
  // En `error` había DOS botones y los dos relanzaban la IA. Este comprueba que los
  // datos ya corregidos se sostienen y devuelve la nómina a revisión, SIN tocar nada de
  // lo que el usuario escribió a mano; «Reintentar IA» sigue aparte, en su propio botón,
  // para cuando lo que se quiere es releer el PDF.
  [NOMINA_STATE_ERROR]: {
    label: 'Validar y revisar',
    icon: 'mdi-check-decagram-outline',
    color: 'primary',
    requiredAction: 'nomina_edit',
    handlerName: 'recoverNomina',
    hint: 'Comprueba los datos corregidos y deja la nómina lista para revisar, sin volver a leer el PDF.',
  },
  [NOMINA_STATE_PENDING_REVIEW]: {
    label: 'Generar asiento',
    icon: 'mdi-book-plus-outline',
    color: 'primary',
    requiredAction: 'nomina_account',
    handlerName: 'generateEntry',
    hint: 'Crea el asiento simplificado con los datos revisados. Todavía no se envía al programa contable.',
  },
  [NOMINA_STATE_ENTRY_GENERATED]: {
    label: 'Contabilizar',
    icon: 'mdi-book-check-outline',
    color: 'success',
    requiredAction: 'nomina_account',
    handlerName: 'accountNomina',
    hint: 'Envía el asiento al programa contable. El proceso continúa en segundo plano.',
  },
};

/**
 * Si aún se puede relanzar la lectura IA: solo ANTES de que exista el asiento.
 *
 * Espejo de `Nomina.can_restart_parse()`. La frontera está en `entry_generated` porque
 * releer **borra el asiento** (el backend vacía su referencia al reintentar): hacerlo con
 * uno ya generado tira el trabajo de revisarlo.
 *
 * Quien quiera releer ese PDF tiene camino: bajar el estado a revisión —que borra el
 * asiento con su aviso explícito (§7)— y reintentar entonces. Así el borrado se decide en
 * un solo sitio, el que lo explica.
 *
 * De `accounting` en adelante hay además la razón de siempre: el asiento ya viaja al
 * programa contable y releer desincronizaría lo contabilizado.
 */
export function canRetryNominaParse(state: string): boolean {
  return ![
    // Esperando a que acepten los cortes: releer se saltaría la separación.
    NOMINA_STATE_PENDING_SPLIT_REVIEW,
    NOMINA_STATE_ENTRY_GENERATED,
    NOMINA_STATE_ACCOUNTING,
    NOMINA_STATE_ACCOUNTING_RUNNING,
    NOMINA_STATE_PENDING_SYNC,
    NOMINA_STATE_ACCOUNTED,
    NOMINA_STATE_ACCOUNTED_MANUAL,
  ].includes(state);
}

export const NOMINA_TAB_DATA = 'data';
export const NOMINA_TAB_ENTRY = 'entry';

/**
 * El tab con el que se abre el detalle: lo que toca hacer con esa nómina.
 *
 * Se mira si el ASIENTO EXISTE, no solo el estado, y esa es la parte que se
 * equivoca fácil: `mark_accounting_failed` deja la nómina en `error` **conservando
 * la referencia** del asiento (falló mandarlo, no crearlo), mientras
 * `mark_entry_generation_failed` la deja en `error` y BORRA la referencia (el
 * asiento no llegó a escribirse). Con un orden lineal de estados, el primer caso
 * abriría el tab de datos y esconderiía justo el asiento que hay que revisar.
 */
export function getDefaultNominaTab(state: string, entryReference: string): string {
  return entryReference ? NOMINA_TAB_ENTRY : NOMINA_TAB_DATA;
}

/** Si el tab del asiento tiene algo que mostrar. Vacío no es un error: es un paso. */
export function hasNominaEntryTabContent(state: string, entryReference: string): boolean {
  return Boolean(entryReference);
}

/**
 * Si se puede editar la FICHA DEL EMPLEADO desde el detalle de esta nómina.
 *
 * Frontera en `accounting`: a partir de ahí el asiento ya viaja al programa contable con
 * el nombre y las cuentas de ese momento, y cambiar la ficha dejaría la nómina diciendo
 * una cosa y lo contabilizado otra, sin forma de saber cuál es la buena.
 *
 * **Ya NO delega en `canRetryNominaParse`**, aunque las dos empezaran igual: releer se
 * cerró un paso antes (con el asiento generado, porque lo borraría), y con la delegación
 * eso arrastró esta regla sin motivo — dejaba de poder tocarse la ficha con el asiento
 * generado, que es justo cuando se revisa y se corrige. Dos reglas que coinciden en parte
 * no son la misma regla; un test lo pilló.
 *
 * Ojo: esto solo cierra el ATAJO desde la nómina. La ficha sigue siendo editable
 * desde el maestro de empleados —es su sitio— y lo que se contabilizó no cambia.
 */
export function canEditEmpleadoFromNomina(state: string): boolean {
  return ![
    NOMINA_STATE_ACCOUNTING,
    NOMINA_STATE_ACCOUNTING_RUNNING,
    NOMINA_STATE_PENDING_SYNC,
    NOMINA_STATE_ACCOUNTED,
    NOMINA_STATE_ACCOUNTED_MANUAL,
  ].includes(state);
}

/**
 * Los campos que la IA lee y el usuario puede cambiar, con el nombre que tienen en el
 * formulario. Solo estos: comparar `state` o `pdf_rotation` no dice nada de una
 * incidencia, y una lista larga esconde las tres filas que importan.
 */
export const AI_READ_COMPARISON_FIELD_LIST: { field: string; label: string }[] = [
  { field: 'employee_name', label: 'Nombre del trabajador' },
  { field: 'employee_dni', label: 'DNI del trabajador' },
  { field: 'period', label: 'Período' },
  { field: 'payment_date', label: 'Fecha de pago' },
  { field: 'gross_amount', label: 'Salario bruto' },
  { field: 'irpf_amount', label: 'IRPF' },
  { field: 'ss_employee_amount', label: 'SS trabajador' },
  { field: 'ss_employer_amount', label: 'SS empresa' },
  { field: 'other_deductions_amount', label: 'Otras deducciones' },
  { field: 'in_kind_amount', label: 'En especie' },
  { field: 'net_amount', label: 'Salario neto' },
];

/**
 * Prefijo de los dos avisos de «no sé quién es este trabajador».
 *
 * Se compara por el PREFIJO y no por el texto entero porque el mensaje lleva el DNI
 * interpolado: cuando se elige la ficha buena, el aviso guardado nombra un DNI que ya no
 * es de nadie. Espejo de `_EMPLEADO_NOT_FOUND_PREFIX` en el dominio del backend.
 */
const EMPLEADO_NOT_FOUND_PREFIX = 'Empleado no encontrado:';

/**
 * Si el aviso guardado es el de «no encuentro al empleado».
 *
 * Sirve para saber cuándo CADUCA: enlazar la ficha lo resuelve, y dejarlo en pantalla
 * contradiría al selector que el usuario acaba de usar. Solo ese aviso — un fallo al
 * contabilizar no lo arregla elegir otro empleado, y esconderlo taparía algo que sigue
 * ahí. Espejo de `Nomina._is_unknown_empleado_error()`.
 */
export function isUnknownEmpleadoError(errorMessage: string): boolean {
  return (errorMessage || '').startsWith(EMPLEADO_NOT_FOUND_PREFIX);
}

/**
 * El aviso de que no hay ficha para el trabajador que leyó la IA.
 *
 * Espejo EXACTO de `EMPLEADO_NOT_FOUND_MESSAGE` / `EMPLEADO_DNI_MISSING_MESSAGE` del
 * backend: si los textos se separan, `isUnknownEmpleadoError` dejaría de reconocer el que
 * escribe la pantalla y el aviso se volvería imborrable.
 */
export function buildUnknownEmpleadoMessage(readDni: string): string {
  const dni = (readDni || '').trim();
  if (!dni) {
    return EMPLEADO_NOT_FOUND_PREFIX + ' no se ha podido leer el DNI del trabajador '
      + 'en la nómina.';
  }
  return EMPLEADO_NOT_FOUND_PREFIX + ' el DNI ' + dni + ' leído en la nómina no '
    + 'corresponde a ningún empleado de la empresa.';
}

/**
 * Los dos campos que **el sistema** sobrescribe al enlazar el empleado.
 *
 * `link_empleado` adopta el nombre y el DNI de la ficha del maestro: el dato bueno es el
 * de la ficha, no el que la IA leyó de un papel que puede venir con una errata (§3.5).
 * Así que en estos dos, "distinto de lo que leyó la IA" NO significa "lo editó alguien".
 */
const EMPLEADO_OWNED_FIELD_SET = new Set(['employee_name', 'employee_dni']);

/** Una fila de la comparación «lo que leyó la IA» ↔ «lo que dice ahora». */
export interface AiReadComparisonRow {
  field: string;
  label: string;
  readValue: string;
  currentValue: string;
  /** Lo cambió una PERSONA después de la lectura. */
  isChanged: boolean;
  /**
   * La diferencia la puso el enlace con la ficha del empleado, no un usuario.
   *
   * Se distingue de `isChanged` en vez de ocultarse: la diferencia es un dato útil
   * —explica por qué la nómina dice un DNI distinto del que se ve en el PDF— pero
   * atribuírsela a una persona que no la hizo convierte el aviso en ruido, y a la
   * tercera fila marcada en falso nadie vuelve a creerse ninguna.
   */
  isFromEmpleado: boolean;
}

/**
 * Compara lo que la IA leyó en su día con lo que la nómina dice ahora.
 *
 * Es la respuesta a la pregunta de toda incidencia: «¿esto lo leyó así la IA o lo
 * cambió alguien?». Sin esto hay que creerse lo que diga quien la tocó.
 *
 * Devuelve vacío cuando no hay snapshot: la nómina no ha pasado por la IA (subida antes
 * de que existiera este registro, o a mano), y una tabla comparando contra vacío diría
 * que "todo cambió", que es falso.
 */
export function buildAiReadComparisonRowList(nomina: Nomina): AiReadComparisonRow[] {
  const snapshot: any = nomina.ai_read_snapshot || {};
  if (!Object.keys(snapshot).length) return [];
  const current: any = nomina;
  const hasEmpleado = Boolean(nomina.empleado_uuid);
  return AI_READ_COMPARISON_FIELD_LIST.map(({ field, label }) => {
    const readValue = snapshot[field];
    const currentValue = current[field];
    const isDifferent = !_isSameReadValue(readValue, currentValue);
    // Con ficha enlazada, una diferencia en nombre o DNI la escribió `link_empleado`.
    // Sin ficha no hay nadie más a quien atribuirla: la escribió una persona.
    const isFromEmpleado = isDifferent && hasEmpleado && EMPLEADO_OWNED_FIELD_SET.has(field);
    return {
      field,
      label,
      readValue: _comparisonText(readValue),
      currentValue: _comparisonText(currentValue),
      isChanged: isDifferent && !isFromEmpleado,
      isFromEmpleado,
    };
  });
}

export function countChangedAiReadFields(nomina: Nomina): number {
  return buildAiReadComparisonRowList(nomina).filter(row => row.isChanged).length;
}

/**
 * Si los dos valores son el mismo dato.
 *
 * Los importes se comparan como NÚMERO: el snapshot guarda `2650` y el formulario puede
 * traer `'2650'` como texto. Marcar eso como cambiado enseñaría una diferencia que no
 * existe y haría dudar de un dato correcto — lo contrario de para qué sirve esto.
 */
function _isSameReadValue(readValue: any, currentValue: any): boolean {
  const readNumber = Number(readValue);
  const currentNumber = Number(currentValue);
  const areBothNumeric = readValue !== '' && currentValue !== ''
    && readValue !== null && currentValue !== null
    && !Number.isNaN(readNumber) && !Number.isNaN(currentNumber);
  if (areBothNumeric) return readNumber === currentNumber;
  return _comparisonText(readValue) === _comparisonText(currentValue);
}

function _comparisonText(value: any): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}


/**
 * La referencia de un asiento concreto en el programa contable.
 *
 * Es la que devuelve el CONECTOR, sea cual sea: este módulo servirá a varios y cada
 * uno numera a su manera, así que aquí no se nombra ninguno ni se compone nada.
 * Vacía mientras no la mande — inventar un identificador manda al usuario a buscar
 * algo que en su programa no existe.
 */
export function getNominaErpReference(nomina: Nomina, kind: string): string {
  if (!nomina) return '';
  if (kind === ENTRY_KIND_PAYMENT) return nomina.erp_payment_reference || '';
  return nomina.erp_registration_reference || '';
}


// Con lo que empieza el aviso de una subida que no se pudo cobrar. Espejo de
// `NOMINA_UNPAID_ERROR_PREFIX` en el backend (`ddd/nominas/domain/nomina_credits.py`):
// el mensaje lo escribe él y aquí solo se reconoce (el diálogo de subida lo usa).
export const NOMINA_UNPAID_ERROR_PREFIX = 'No tiene créditos suficientes';

/** Si la nómina está sin cobrar, esperando a que el cliente la procese. */
export function isNominaUnpaid(nomina: { state?: string }): boolean {
  if (!nomina) return false;
  return nomina.state === NOMINA_STATE_PENDING_PAYMENT;
}

/**
 * Si se puede hacer algo con ella: revisarla, editarla, validarla, contabilizar.
 *
 * Lo único que lo impide hoy es no haberla podido cobrar. Sin cobrar no se da el
 * servicio — dejar revisarla o contabilizarla sería regalar justo el trabajo que no se
 * ha pagado. Se desbloquea con «Procesar», que la cobra y la manda a leer.
 */
export function canWorkOnNomina(nomina: { state?: string }): boolean {
  return !isNominaUnpaid(nomina);
}

/**
 * El líquido que deberían dar los importes: bruto − IRPF − SS trabajador − otras
 * deducciones. Espejo del cuadre de `Nomina.validate_for_entry` en el backend.
 *
 * La SS a cargo de la EMPRESA no entra: no se le descuenta al trabajador.
 */
export function nominaExpectedNet(nomina: {
  gross_amount?: number;
  irpf_amount?: number;
  ss_employee_amount?: number;
  other_deductions_amount?: number;
}): number {
  return (nomina.gross_amount || 0)
    - (nomina.irpf_amount || 0)
    - (nomina.ss_employee_amount || 0)
    - (nomina.other_deductions_amount || 0);
}

/** Si es el resumen de toda la plantilla y no el recibo de un trabajador. */
export function isSummaryNomina(nomina: { nomina_kind?: string }): boolean {
  return (nomina && nomina.nomina_kind) === NOMINA_KIND_SUMMARY;
}

/** El nombre en pantalla de los campos a los que la IA puede llevar una cabecera. */
const SUMMARY_FIELD_LABEL_MAP: { [field: string]: string } = {
  company_cost_amount: 'Coste empresa',
};

/** Una fila de «Cabeceras leídas»: la columna del PDF y adónde fue. */
export interface SummaryHeaderRow {
  header: string;
  fieldLabel: string;
  isIgnored: boolean;
}

/**
 * Qué cabecera del resumen fue a qué campo, para enseñarlo en el detalle. Las que no
 * fueron a ningún campo también salen: ver que se descartó explica por qué un total es 0.
 */
export function buildSummaryHeaderRowList(nomina: Nomina): SummaryHeaderRow[] {
  return (nomina.summary_header_map || []).map((mapping) => {
    const isIgnored = mapping.field === SUMMARY_IGNORED_FIELD;
    const comparisonField = AI_READ_COMPARISON_FIELD_LIST.find(item => item.field === mapping.field);
    const fieldLabel = isIgnored
      ? 'Sin usar'
      : (comparisonField ? comparisonField.label : (SUMMARY_FIELD_LABEL_MAP[mapping.field] || mapping.field));
    return { header: mapping.header, fieldLabel, isIgnored };
  });
}

/** Todo lo que se le pide a la IA, con su nombre en pantalla, para el popup «Lectura IA». */
export const AI_READ_FIELD_LIST: { field: string; label: string; kind: string }[] = [
  { field: 'employee_name', label: 'Nombre del trabajador', kind: 'text' },
  { field: 'employee_dni', label: 'DNI del trabajador', kind: 'text' },
  { field: 'period', label: 'Período', kind: 'text' },
  { field: 'payment_date', label: 'Fecha de pago', kind: 'text' },
  { field: 'gross_amount', label: 'Salario bruto', kind: 'amount' },
  { field: 'irpf_amount', label: 'IRPF', kind: 'amount' },
  { field: 'irpf_rate', label: 'Tipo de IRPF', kind: 'percent' },
  { field: 'ss_employee_amount', label: 'Total aportaciones del trabajador (escrito)', kind: 'amount' },
  { field: 'employee_common_contingencies', label: 'Cotización trabajador · Contingencias comunes', kind: 'amount' },
  { field: 'employee_mei', label: 'Cotización trabajador · MEI', kind: 'amount' },
  { field: 'employee_unemployment', label: 'Cotización trabajador · Desempleo', kind: 'amount' },
  { field: 'employee_vocational_training', label: 'Cotización trabajador · Formación profesional', kind: 'amount' },
  { field: 'employee_overtime_force_majeure', label: 'Cotización trabajador · Horas extra por fuerza mayor', kind: 'amount' },
  { field: 'employee_overtime', label: 'Cotización trabajador · Otras horas extra', kind: 'amount' },
  { field: 'employee_solidarity', label: 'Cotización trabajador · Solidaridad', kind: 'amount' },
  { field: 'other_deductions_amount', label: 'Total otras deducciones (escrito)', kind: 'amount' },
  { field: 'in_kind_amount', label: 'Retribución en especie (dentro del bruto)', kind: 'amount' },
  { field: 'deduction_in_kind_amount', label: 'Otras deducciones · Descuento en especie', kind: 'amount' },
  { field: 'deduction_advance_amount', label: 'Otras deducciones · Anticipos', kind: 'amount' },
  { field: 'deduction_staff_loan_amount', label: 'Otras deducciones · Préstamos al personal', kind: 'amount' },
  { field: 'deduction_garnishment_amount', label: 'Otras deducciones · Embargos', kind: 'amount' },
  { field: 'deduction_union_fee_amount', label: 'Otras deducciones · Cuota sindical', kind: 'amount' },
  { field: 'deduction_other_amount', label: 'Otras deducciones · Otras', kind: 'amount' },
  { field: 'net_amount', label: 'Líquido a percibir', kind: 'amount' },
  { field: 'ss_employer_amount', label: 'Total aportación empresarial (escrito)', kind: 'amount' },
  { field: 'company_cost_amount', label: 'Coste empresa', kind: 'amount' },
  { field: 'employer_common_contingencies', label: 'Aportación empresa · Contingencias comunes', kind: 'amount' },
  { field: 'employer_mei', label: 'Aportación empresa · MEI', kind: 'amount' },
  { field: 'employer_work_accidents', label: 'Aportación empresa · AT y EP', kind: 'amount' },
  { field: 'employer_unemployment', label: 'Aportación empresa · Desempleo', kind: 'amount' },
  { field: 'employer_vocational_training', label: 'Aportación empresa · Formación profesional', kind: 'amount' },
  { field: 'employer_fogasa', label: 'Aportación empresa · FOGASA', kind: 'amount' },
  { field: 'employer_overtime', label: 'Aportación empresa · Horas extraordinarias', kind: 'amount' },
  { field: 'employer_solidarity', label: 'Aportación empresa · Solidaridad', kind: 'amount' },
  { field: 'employee_hire_date', label: 'Fecha de antigüedad', kind: 'text' },
  { field: 'employee_job_title', label: 'Categoría o puesto', kind: 'text' },
  { field: 'employee_email', label: 'Email del trabajador', kind: 'text' },
  { field: 'employee_phone', label: 'Teléfono del trabajador', kind: 'text' },
];

export interface AiReadRow {
  field: string;
  label: string;
  /** Tal cual lo devolvió la IA, en texto. '' si no lo encontró. */
  value: string;
  /** No lo encontró en el PDF (vacío, o 0 en un importe). */
  isEmpty: boolean;
}

/**
 * Lo que leyó la IA, campo a campo, TAMBIÉN los que dejó vacíos.
 *
 * Es lo que ve el cliente en el popup «Lectura IA»: que sepa exactamente qué salió del
 * PDF, y qué no encontró, sin fiarse de lo que haya ahora en el formulario.
 */
export function buildAiReadRowList(snapshot: Record<string, any>): AiReadRow[] {
  const read: any = snapshot || {};
  return AI_READ_FIELD_LIST.map(({ field, label, kind }) => {
    const raw = read[field];
    const isEmpty = kind === 'amount' || kind === 'percent'
      ? !Number(raw)
      : !String(raw === null || raw === undefined ? '' : raw).trim();
    const value = isEmpty ? '' : _formatReadValue(raw, kind);
    return { field, label, value, isEmpty };
  });
}

function _formatReadValue(raw: any, kind: string): string {
  if (kind === 'amount') return _formatAmount(Number(raw));
  // «16,34 %»: un porcentaje con el símbolo de euro se leería como un importe.
  if (kind === 'percent') return String(Number(raw)).replace('.', ',') + ' %';
  return String(raw).trim();
}

function _formatAmount(amount: number): string {
  const [integerPart, decimalPart] = amount.toFixed(2).split('.');
  return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + decimalPart + ' €';
}

/** Una cifra que calculó ADP porque el PDF no la traía: qué es, cuánto y cómo salió. */
export interface AdpComputedRow {
  label: string;
  value: string;
  /** La cuenta hecha, con las cifras del PDF: «1.182,63 € − 880,91 €». */
  formula: string;
  /** Lo que se comprobó, si se comprobó algo. Vacío si no. */
  check: string;
  /** La comprobación no cuadró (se enseña como aviso). */
  isMismatch: boolean;
}

/**
 * Lo que calculó ADP, para la sección «Calculado por ADP» del popup de la lectura.
 *
 * Hoy solo la SS de la empresa: el PDF no la trae como una cifra, y ADP la saca del coste
 * empresa o del desglose de la aportación. Vacío si se usó lo que estaba escrito.
 */
export function buildAdpComputedRowList(nomina: Nomina): AdpComputedRow[] {
  const contribution = (nomina && nomina.employer_contribution) || {};
  const source = contribution.source || '';
  if (source !== 'company_cost' && source !== 'breakdown') return [];
  const amount = Number(contribution.amount) || 0;
  const breakdownTotal = Number(contribution.breakdown_total) || 0;
  if (source === 'company_cost') {
    const companyCost = Number(contribution.company_cost) || 0;
    const gross = companyCost - (Number(contribution.company_cost_total) || 0);
    const hasBreakdown = breakdownTotal > 0;
    return [{
      label: 'SS empresa',
      value: _formatAmount(amount),
      formula: 'Coste empresa − salario bruto = ' + _formatAmount(companyCost) + ' − '
        + _formatAmount(gross),
      check: hasBreakdown
        ? 'Suma del desglose de la aportación: ' + _formatAmount(breakdownTotal)
          + (contribution.is_mismatch ? ' (no coincide)' : ' (coincide)')
        : '',
      isMismatch: Boolean(contribution.is_mismatch),
    }];
  }
  return [{
    label: 'SS empresa',
    value: _formatAmount(amount),
    formula: 'Suma del desglose de la aportación de la empresa',
    check: '',
    isMismatch: false,
  }];
}

/**
 * La comprobación de una SS empresa que SÍ venía escrita (en un resumen): coste empresa −
 * salario bruto, que tiene que dar lo mismo. Solo si había coste empresa con que comprobarla;
 * `null` si no, o si la cifra la calculó ADP (eso lo explica `buildAdpComputedRowList`).
 */
export function buildEmployerContributionCheck(nomina: Nomina): { text: string; isMismatch: boolean } | null {
  const contribution = (nomina && nomina.employer_contribution) || {};
  if (contribution.source !== 'read') return null;
  const companyCostTotal = Number(contribution.company_cost_total) || 0;
  if (!companyCostTotal) return null;
  const companyCost = Number(contribution.company_cost) || 0;
  const gross = companyCost - companyCostTotal;
  return {
    text: 'Coste empresa − salario bruto = ' + _formatAmount(companyCost) + ' − ' + _formatAmount(gross)
      + ' = ' + _formatAmount(companyCostTotal)
      + (contribution.is_mismatch ? ' (no coincide)' : ' (coincide)'),
    isMismatch: Boolean(contribution.is_mismatch),
  };
}

/**
 * Cómo sacó ADP la SS trabajador, para explicarlo debajo de su importe. Solo cuando la
 * nómina no escribía el total y ADP sumó las cotizaciones; `null` si estaba escrito.
 *
 * La fórmula lleva las cifras de las líneas, las que se ven en el PDF: así el cliente
 * las encuentra una a una sin buscar un total que no existe.
 */
export function buildEmployeeContributionRow(nomina: Nomina): AdpComputedRow | null {
  const contribution = (nomina && nomina.employee_contribution) || {};
  if (contribution.source !== 'breakdown') return null;
  const amountList = (contribution.breakdown_line_list || [])
    .map(line => Number(line.amount) || 0)
    .filter(amount => amount > 0);
  return {
    label: 'SS trabajador',
    value: _formatAmount(Number(contribution.amount) || 0),
    formula: 'suma de las cotizaciones del trabajador ('
      + amountList.map(amount => _formatAmount(amount)).join(' + ') + ')',
    check: '',
    isMismatch: false,
  };
}
